import { createContext, useContext, useEffect, useMemo, useState } from "react";

const AuthContext = createContext(null);

const LS_USERS = "cc_demo_users";
const LS_CURRENT_UID = "cc_demo_current_uid";
const LS_LANG = "cc_lang";
const LS_REPORTS = "cc_demo_reports";
const LS_BLOGS = "cc_demo_blogs";

const readJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeJSON = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

const makeId = (prefix = "id") =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const nowISO = () => new Date().toISOString();

const defaultProfile = ({ uid, name, email, lang }) => ({
  uid,
  name,
  email,
  role: null,
  lang: lang || "en",
  isAdmin: false,
  pts: 0,
  streak: 0,
  lastReportDate: null,
  reports: 0,
  cleanups: 0,
  badgesEarned: [],
  claimedChallenges: [],
  redeemedRewards: [],
  notifications: [],
  createdAt: nowISO(),
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLangState] = useState(() => localStorage.getItem(LS_LANG) || "en");
  const [notifs, setNotifs] = useState([]);

  // ---------- storage helpers ----------
  const getAllUsersLocal = () => readJSON(LS_USERS, []);
  const saveAllUsersLocal = (users) => writeJSON(LS_USERS, users);

  const getCurrentRecord = () => {
    const uid = localStorage.getItem(LS_CURRENT_UID);
    if (!uid) return null;
    return getAllUsersLocal().find((u) => u.uid === uid) || null;
  };

  const setCurrentRecord = (record) => {
    localStorage.setItem(LS_CURRENT_UID, record.uid);
    setUser({ uid: record.uid, email: record.email, displayName: record.name });
    setProfile(record);
    setNotifs(record.notifications || []);
    if (record.lang) {
      localStorage.setItem(LS_LANG, record.lang);
      setLangState(record.lang);
    }
  };

  const updateCurrentRecord = (updater) => {
    const users = getAllUsersLocal();
    const uid = localStorage.getItem(LS_CURRENT_UID);
    if (!uid) return null;

    const idx = users.findIndex((u) => u.uid === uid);
    if (idx === -1) return null;

    const current = users[idx];
    const next = typeof updater === "function" ? updater(current) : { ...current, ...updater };
    users[idx] = next;
    saveAllUsersLocal(users);
    setCurrentRecord(next);
    return next;
  };

  const pushNotifLocal = (uid, { title, body, icon = "notifications", type = "system" }) => {
    const users = getAllUsersLocal();
    const idx = users.findIndex((u) => u.uid === uid);
    if (idx === -1) return;

    const item = {
      id: makeId("notif"),
      title,
      body,
      icon,
      type,
      read: false,
      createdAt: nowISO(),
    };

    users[idx].notifications = [item, ...(users[idx].notifications || [])];
    saveAllUsersLocal(users);

    if (localStorage.getItem(LS_CURRENT_UID) === uid) {
      setNotifs(users[idx].notifications);
      setProfile(users[idx]);
      setUser({ uid: users[idx].uid, email: users[idx].email, displayName: users[idx].name });
    }
  };

  // ---------- init ----------
  useEffect(() => {
    const existing = getCurrentRecord();
    if (existing) {
      setCurrentRecord(existing);
    } else {
      setUser(null);
      setProfile(null);
      setNotifs([]);
    }
    setLoading(false);
  }, []);

  // ---------- language ----------
  const setLang = (l) => {
    localStorage.setItem(LS_LANG, l);
    setLangState(l);

    const uid = localStorage.getItem(LS_CURRENT_UID);
    if (!uid) return;

    updateCurrentRecord((current) => ({
      ...current,
      lang: l,
    }));
  };

  // ---------- auth ----------
  const register = async ({ name, email, password, selectedLang }) => {
    const users = getAllUsersLocal();
    const normalizedEmail = String(email).trim().toLowerCase();

    const exists = users.some((u) => u.email.toLowerCase() === normalizedEmail);
    if (exists) {
      throw new Error("An account with this email already exists.");
    }

    const uid = makeId("user");
    const currentLang = selectedLang || lang;

    const record = {
      ...defaultProfile({
        uid,
        name: String(name).trim(),
        email: normalizedEmail,
        lang: currentLang,
      }),
      password, // demo mode only
    };

    users.push(record);
    saveAllUsersLocal(users);
    setCurrentRecord(record);

    pushNotifLocal(uid, {
      title: `Welcome, ${record.name?.split(" ")[0]}`,
      body: "Choose your role next — it decides which screens you see.",
      icon: "celebration",
      type: "system",
    });

    return { uid, email: normalizedEmail, displayName: record.name };
  };

  const login = async ({ email, password }) => {
    const users = getAllUsersLocal();
    const normalizedEmail = String(email).trim().toLowerCase();

    const found = users.find(
      (u) => u.email.toLowerCase() === normalizedEmail && u.password === password
    );

    if (!found) {
      throw new Error("Invalid email or password.");
    }

    setCurrentRecord(found);

    pushNotifLocal(found.uid, {
      title: `Welcome back, ${found.name?.split(" ")[0]}`,
      body: "Anything you captured offline will upload as soon as you have signal.",
      icon: "waving_hand",
      type: "system",
    });

    return { uid: found.uid, email: found.email, displayName: found.name };
  };

  const logout = async () => {
    localStorage.removeItem(LS_CURRENT_UID);
    setUser(null);
    setProfile(null);
    setNotifs([]);
  };

  // ---------- role ----------
  const updateRole = async (role) => {
    if (!profile) return;

    const roleLabel =
      role === "cleanup"
        ? "Cleanup Crew"
        : role === "ngo"
        ? "NGO Coordinator"
        : "Community Reporter";

    updateCurrentRecord((current) => ({
      ...current,
      role,
    }));

    pushNotifLocal(profile.uid, {
      title: `Role set: ${roleLabel}`,
      body: "Your dashboard is ready.",
      icon:
        role === "cleanup"
          ? "cleaning_services"
          : role === "ngo"
          ? "verified_user"
          : "photo_camera",
      type: "system",
    });
  };

  // ---------- notifications ----------
  const markNotifRead = async (notifId) => {
    updateCurrentRecord((current) => ({
      ...current,
      notifications: (current.notifications || []).map((n) =>
        n.id === notifId ? { ...n, read: true } : n
      ),
    }));
  };

  const markAllRead = async () => {
    updateCurrentRecord((current) => ({
      ...current,
      notifications: (current.notifications || []).map((n) => ({ ...n, read: true })),
    }));
  };

  // ---------- points / progress ----------
  const addPoints = async (amount, reason = "") => {
    if (!profile) return 0;

    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();

    const next = updateCurrentRecord((current) => {
      const newPts = (current.pts || 0) + amount;
      const last = current.lastReportDate;
      const newStreak =
        last === yesterday ? (current.streak || 0) + 1 : last === today ? current.streak || 0 : 1;

      return {
        ...current,
        pts: newPts,
        streak: newStreak,
        lastReportDate: today,
      };
    });

    pushNotifLocal(profile.uid, {
      title: `+${amount} points`,
      body: reason || "Recorded on your account.",
      icon: "stars",
      type: "points",
    });

    return next?.pts || 0;
  };

  const incrementReports = async () => {
    updateCurrentRecord((current) => ({
      ...current,
      reports: (current.reports || 0) + 1,
    }));
  };

  const incrementCleanups = async () => {
    updateCurrentRecord((current) => ({
      ...current,
      cleanups: (current.cleanups || 0) + 1,
    }));
  };

  const earnBadge = async (badgeId, badgeLabel = "") => {
    if (!profile) return;
    if ((profile.badgesEarned || []).includes(badgeId)) return;

    updateCurrentRecord((current) => ({
      ...current,
      badgesEarned: [...(current.badgesEarned || []), badgeId],
    }));

    pushNotifLocal(profile.uid, {
      title: `Badge earned: ${badgeLabel || badgeId}`,
      body: "It is in your badge collection, under Profile.",
      icon: "military_tech",
      type: "badge",
    });
  };

  const claimChallenge = async (challengeId, reward, label = "") => {
    if (!profile) return;

    updateCurrentRecord((current) => ({
      ...current,
      claimedChallenges: [...(current.claimedChallenges || []), challengeId],
    }));

    await addPoints(reward, label ? `Challenge complete: ${label}` : "Challenge complete!");
  };

  const redeemReward = async (rewardId, rewardName = "") => {
    if (!profile) return;

    updateCurrentRecord((current) => ({
      ...current,
      redeemedRewards: [...(current.redeemedRewards || []), rewardId],
    }));

    pushNotifLocal(profile.uid, {
      title: `Reward redeemed: ${rewardName}!`,
      body: `We'll contact you at ${profile?.email} within 48 hours.`,
      icon: "card_giftcard",
      type: "reward",
    });
  };

  // ---------- admin ----------
  const sendAdminNotif = async ({ title, body, icon = "campaign", targetUid = null }) => {
    if (!profile) return;

    if (targetUid) {
      pushNotifLocal(targetUid, { title, body, icon, type: "admin" });
      return;
    }

    const users = getAllUsersLocal();
    users.forEach((u) => {
      const item = {
        id: makeId("notif"),
        title,
        body,
        icon,
        type: "admin",
        read: false,
        createdAt: nowISO(),
      };
      u.notifications = [item, ...(u.notifications || [])];
    });
    saveAllUsersLocal(users);

    const current = getCurrentRecord();
    if (current) setCurrentRecord(current);
  };

  const postBlog = async ({ title, content, excerpt, coverImage, category, readTime, tags }) => {
    if (!profile?.isAdmin) return null;

    const blogs = readJSON(LS_BLOGS, []);
    const blog = {
      id: makeId("blog"),
      title,
      content,
      excerpt: excerpt || `${content?.slice(0, 150) || ""}…`,
      coverImage: coverImage || "",
      category: category || "action",
      readTime: readTime || "5 min read",
      tags: tags || [],
      author: profile.name,
      authorId: profile.uid,
      published: true,
      createdAt: nowISO(),
    };
    blogs.unshift(blog);
    writeJSON(LS_BLOGS, blogs);
    return blog.id;
  };

  const getAllUsers = async () => {
    return getAllUsersLocal().map(({ password, ...rest }) => rest);
  };

  const getAllReports = async () => {
    return readJSON(LS_REPORTS, []);
  };

  const refreshProfile = () => {
    const current = getCurrentRecord();
    if (current) setCurrentRecord(current);
  };

  const unreadCount = useMemo(
    () => (notifs || []).filter((n) => !n.read).length,
    [notifs]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        lang,
        notifs,
        unreadCount,
        setLang,
        register,
        login,
        logout,
        updateRole,
        addPoints,
        incrementReports,
        incrementCleanups,
        claimChallenge,
        redeemReward,
        earnBadge,
        pushNotif: (uid, payload) => pushNotifLocal(uid, payload),
        markNotifRead,
        markAllRead,
        sendAdminNotif,
        postBlog,
        getAllUsers,
        getAllReports,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);